import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControl,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { 
  getAllFixVersions, 
  createFixVersion, 
  updateFixVersion, 
  deleteFixVersion,
  authGet
} from '../../services/apiService';
import { formatDate } from '../../utils/formatters';

/**
 * FixVersionsManagement component for managing fix versions
 * @returns {JSX.Element} FixVersionsManagement component
 */
const FixVersionsManagement = () => {
  // State for fix versions
  const [fixVersions, setFixVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for dialogs
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  
  // State for form data
  const [formData, setFormData] = useState({
    version: '',
    description: '',
    releaseDate: '',
    adoTestPlanFolderId: '',
    active: true,
    isMaintenanceRelease: false,
    releaseType: 'Maintenance'
  });
  
  // State for selected fix version
  const [selectedFixVersion, setSelectedFixVersion] = useState(null);
  
  // Fetch fix versions on component mount
  useEffect(() => {
    fetchFixVersions();
  }, []);
  
  // Fetch fix versions from API (admin - gets all fix versions including inactive)
  const fetchFixVersions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use authGet to get all fix versions (including inactive) for admin
      const response = await authGet('/api/fix-versions');
      
      // Handle different response formats
      let fixVersionsData = [];
      if (response && response.data && Array.isArray(response.data)) {
        fixVersionsData = response.data;
      } else if (response && response.data && response.data.data && Array.isArray(response.data.data)) {
        fixVersionsData = response.data.data;
      } else if (Array.isArray(response)) {
        fixVersionsData = response;
      }
      
      if (fixVersionsData.length >= 0) {
        // Debug: Log first fix version to see structure
        if (fixVersionsData.length > 0) {
          console.log('Sample fix version from API:', fixVersionsData[0]);
          console.log('Sample fix version _id:', fixVersionsData[0]._id, 'type:', typeof fixVersionsData[0]._id);
        }
        setFixVersions(fixVersionsData);
      } else {
        setError('Failed to load fix versions. Invalid response format.');
      }
    } catch (err) {
      console.error('Error fetching fix versions:', err);
      setError('Failed to load fix versions. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    
    // For switch components, use checked instead of value
    if (type === 'checkbox' || name === 'active') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  // Handle add dialog open
  const handleOpenAddDialog = () => {
    setFormData({
      version: '',
      description: '',
      releaseDate: '',
      adoTestPlanFolderId: '',
      active: true,
      isMaintenanceRelease: false,
      releaseType: 'Maintenance'
    });
    setOpenAddDialog(true);
  };
  
  // Handle edit dialog open
  const handleOpenEditDialog = (fixVersion) => {
    setSelectedFixVersion(fixVersion);
    setFormData({
      version: fixVersion.version,
      description: fixVersion.description || '',
      releaseDate: fixVersion.releaseDate ? new Date(fixVersion.releaseDate).toISOString().split('T')[0] : '',
      adoTestPlanFolderId: fixVersion.adoTestPlanFolderId || '',
      isMaintenanceRelease: fixVersion.isMaintenanceRelease || false,
      releaseType: fixVersion.releaseType || 'Maintenance'
    });
    setOpenEditDialog(true);
  };

  // Handle toggle active status inline
  const handleToggleActive = async (fixVersion) => {
    try {
      const newActiveStatus = !fixVersion.active;
      
      // Use version string instead of _id for stability across database restores
      const version = fixVersion.version;
      
      if (!version) {
        console.error('Invalid release - no version found in fixVersion:', fixVersion);
        setError('Invalid release version. Cannot update.');
        return;
      }
      
      console.log('Toggle active - using version:', version, 'new status:', newActiveStatus);
      
      // Optimistically update the UI
      setFixVersions(prevVersions => 
        prevVersions.map(fv => 
          fv.version === version 
            ? { ...fv, active: newActiveStatus }
            : fv
        )
      );
      
      // Update on server using version string
      await updateFixVersion(version, { active: newActiveStatus });
      
      // Refresh to ensure consistency
      await fetchFixVersions();
    } catch (err) {
      console.error('Error toggling active status:', err);
      setError(`Failed to update active status: ${err.message || 'Please try again.'}`);
      // Revert optimistic update on error
      await fetchFixVersions();
    }
  };
  
  // Handle delete dialog open
  const handleOpenDeleteDialog = (fixVersion) => {
    setSelectedFixVersion(fixVersion);
    setOpenDeleteDialog(true);
  };
  
  // Handle dialog close
  const handleCloseDialog = () => {
    setOpenAddDialog(false);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
    setSelectedFixVersion(null);
  };
  
  // Handle add fix version
  const handleAddFixVersion = async () => {
    try {
      setLoading(true);
      
      // Validate form data
      if (!formData.version) {
        setError('Version is required');
        setLoading(false);
        return;
      }
      
      // Create fix version
      await createFixVersion(formData);
      
      // Refresh fix versions
      await fetchFixVersions();
      
      // Close dialog
      handleCloseDialog();
    } catch (err) {
      console.error('Error adding fix version:', err);
      setError('Failed to add fix version. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle edit fix version
  const handleEditFixVersion = async () => {
    try {
      setLoading(true);
      
      // Validate form data
      if (!formData.version) {
        setError('Version is required');
        setLoading(false);
        return;
      }
      
      // Update fix version using version string instead of _id
      await updateFixVersion(selectedFixVersion.version, formData);
      
      // Refresh fix versions
      await fetchFixVersions();
      
      // Close dialog
      handleCloseDialog();
    } catch (err) {
      console.error('Error updating fix version:', err);
      setError('Failed to update fix version. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle delete fix version
  const handleDeleteFixVersion = async () => {
    try {
      setLoading(true);
      
      // Delete fix version using version string instead of _id
      await deleteFixVersion(selectedFixVersion.version);
      
      // Refresh fix versions
      await fetchFixVersions();
      
      // Close dialog
      handleCloseDialog();
    } catch (err) {
      console.error('Error deleting fix version:', err);
      setError('Failed to delete fix version. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Check if a version is valid (5.9.00 and above)
  const isValidVersion = (version) => {
    const regex = /^(\d+)\.(\d+)\.(\d+)$/;
    const match = version.match(regex);
    if (!match) return false;
    
    const [, major, minor, patch] = match;
    return (
      parseInt(major) > 5 || 
      (parseInt(major) === 5 && parseInt(minor) > 9) || 
      (parseInt(major) === 5 && parseInt(minor) === 9 && parseInt(patch) >= 0)
    );
  };

  // Sort fix versions by release date (newest first, older at bottom)
  const sortedFixVersions = useMemo(() => {
    return [...fixVersions].sort((a, b) => {
      // Handle null/undefined release dates - put them at the bottom
      if (!a.releaseDate && !b.releaseDate) return 0;
      if (!a.releaseDate) return 1; // a goes to bottom
      if (!b.releaseDate) return -1; // b goes to bottom
      
      // Sort by date descending (newest first)
      const dateA = new Date(a.releaseDate);
      const dateB = new Date(b.releaseDate);
      return dateB - dateA;
    });
  }, [fixVersions]);
  
  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">
          Releases Management
        </Typography>
        
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenAddDialog}
        >
          Add Release
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {loading && !openAddDialog && !openEditDialog && !openDeleteDialog ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Version</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Release Date</TableCell>
                <TableCell>Release Type</TableCell>
                <TableCell>ADO TestPlan ID</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Updated At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedFixVersions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No releases found
                  </TableCell>
                </TableRow>
              ) : (
                sortedFixVersions.map((fixVersion) => (
                  <TableRow key={fixVersion._id}>
                    <TableCell>
                      {fixVersion.version}
                      {isValidVersion(fixVersion.version) && (
                        <Typography variant="caption" color="primary" sx={{ ml: 1 }}>
                          (Valid)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{fixVersion.description || '-'}</TableCell>
                    <TableCell>{fixVersion.releaseDate ? formatDate(fixVersion.releaseDate) : '-'}</TableCell>
                    <TableCell>{fixVersion.releaseType || 'Maintenance'}</TableCell>
                    <TableCell>{fixVersion.adoTestPlanFolderId || '-'}</TableCell>
                    <TableCell>
                      <Switch
                        checked={fixVersion.active === true}
                        onChange={() => handleToggleActive(fixVersion)}
                        color="primary"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(fixVersion.createdAt)}</TableCell>
                    <TableCell>{formatDate(fixVersion.updatedAt)}</TableCell>
                    <TableCell>
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenEditDialog(fixVersion)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleOpenDeleteDialog(fixVersion)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Add Release Dialog */}
      <Dialog open={openAddDialog} onClose={handleCloseDialog}>
        <DialogTitle>Add Release</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter the details for the new release. The version should be in the format X.Y.Z (e.g., 5.9.00).
          </DialogContentText>
          
          <TextField
            autoFocus
            margin="dense"
            name="version"
            label="Version"
            type="text"
            fullWidth
            value={formData.version}
            onChange={handleInputChange}
            required
            helperText={formData.version && !isValidVersion(formData.version) ? 'Version should be 5.9.00 or higher' : ''}
            error={formData.version && !isValidVersion(formData.version)}
          />
          
          <TextField
            margin="dense"
            name="description"
            label="Description"
            type="text"
            fullWidth
            value={formData.description}
            onChange={handleInputChange}
          />
          
          <TextField
            margin="dense"
            name="releaseDate"
            label="Release Date"
            type="date"
            fullWidth
            value={formData.releaseDate}
            onChange={handleInputChange}
            InputLabelProps={{
              shrink: true,
            }}
          />
          
          <FormControl fullWidth margin="dense">
            <InputLabel id="release-type-add-label">Release Type</InputLabel>
            <Select
              labelId="release-type-add-label"
              name="releaseType"
              value={formData.releaseType}
              onChange={handleInputChange}
              label="Release Type"
            >
              <MenuItem value="Maintenance">Maintenance</MenuItem>
              <MenuItem value="Feature">Feature</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            margin="dense"
            name="adoTestPlanFolderId"
            label="ADO TestPlan ID"
            type="text"
            fullWidth
            value={formData.adoTestPlanFolderId}
            onChange={handleInputChange}
            helperText="Enter the Azure DevOps TestPlan ID for this release"
          />
          
          <FormControl fullWidth margin="dense">
            <FormControlLabel
              control={
                <Switch
                  checked={formData.active === true}
                  onChange={handleInputChange}
                  name="active"
                  color="primary"
                />
              }
              label="Active"
            />
          </FormControl>
          
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleAddFixVersion} 
            color="primary"
            disabled={!formData.version || !isValidVersion(formData.version) || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Edit Release Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseDialog}>
        <DialogTitle>Edit Release</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Update the details for the release.
          </DialogContentText>
          
          <TextField
            autoFocus
            margin="dense"
            name="version"
            label="Version"
            type="text"
            fullWidth
            value={formData.version}
            onChange={handleInputChange}
            required
            helperText={formData.version && !isValidVersion(formData.version) ? 'Version should be 5.9.00 or higher' : ''}
            error={formData.version && !isValidVersion(formData.version)}
          />
          
          <TextField
            margin="dense"
            name="description"
            label="Description"
            type="text"
            fullWidth
            value={formData.description}
            onChange={handleInputChange}
          />
          
          <TextField
            margin="dense"
            name="releaseDate"
            label="Release Date"
            type="date"
            fullWidth
            value={formData.releaseDate}
            onChange={handleInputChange}
            InputLabelProps={{
              shrink: true,
            }}
          />
          
          <FormControl fullWidth margin="dense">
            <InputLabel id="release-type-edit-label">Release Type</InputLabel>
            <Select
              labelId="release-type-edit-label"
              name="releaseType"
              value={formData.releaseType}
              onChange={handleInputChange}
              label="Release Type"
            >
              <MenuItem value="Maintenance">Maintenance</MenuItem>
              <MenuItem value="Feature">Feature</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            margin="dense"
            name="adoTestPlanFolderId"
            label="ADO TestPlan ID"
            type="text"
            fullWidth
            value={formData.adoTestPlanFolderId}
            onChange={handleInputChange}
            helperText="Enter the Azure DevOps TestPlan ID for this release"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleEditFixVersion} 
            color="primary"
            disabled={!formData.version || !isValidVersion(formData.version) || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Release Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDialog}>
        <DialogTitle>Delete Release</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the release "{selectedFixVersion?.version}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteFixVersion} 
            color="error"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FixVersionsManagement;
